/**
 * Form-archetype core — the React-free half of the create/edit form contract.
 *
 * ── Why this module exists ────────────────────────────────────────────
 *
 * `packages/test-preset` stubs `@tyndo/ui-kit` for every module render suite.
 * When a stub REIMPLEMENTS a shipped hook instead of sharing its code, the
 * two drift, and the drift is invisible until it inverts the harness: a view
 * that is CORRECT against the real module crashes under the stub, so the
 * suite punishes correct code and rewards code written against the stub's
 * accident. That is exactly what happened here — the stub's
 * `useOptimisticRollback` was an unrelated `(initial) => { value, apply,
 * rollback, commit }` cache helper, and its `applyServerErrors` handed
 * `setError` an OBJECT where {@link mapServerErrors} hands it a STRING.
 *
 * The structural fix is the same one `degraded-state-core.ts` uses: the
 * behaviour lives in ONE dependency-free module that both the shipped hook
 * and the stub import. There is nothing left for the stub to reimplement, so
 * there is nothing left to drift.
 *
 * Kept free of React and of every other import so it can be pulled into a
 * CJS jest stub, a `node --test` sweep, or the browser bundle identically.
 * The React envelope lives in `use-form-archetype.tsx`.
 */

// ─── Server error mapping ─────────────────────────────────────────────

/**
 * Canonical RFC 7807 + ASP.NET `ModelStateDictionary` field-error map
 * shape. Both `errors` and `validationErrors` are supported so the helper
 * works with .NET 9 + .NET 10 backends + manual `Problem` instances.
 */
export interface ServerErrorPayload {
  errors?: Record<string, string[]>;
  validationErrors?: Record<string, string[]>;
  title?: string;
  detail?: string;
  status?: number;
}

/**
 * The field-error sink a form hands to {@link mapServerErrors}.
 *
 * `message` is a plain STRING. A sink that receives an object renders it as
 * a React child and throws; the stub used to do exactly that.
 */
export type SetFieldError = (field: string, message: string) => void;

/**
 * Walk a `ProblemDetails`-shaped server error and pipe each `field →
 * message` pair into `setError`. The canonical shape produced by the
 * Tyndo gateway is `{ errors: { fieldA: ["msg1"], fieldB: [...] } }`;
 * `validationErrors` is a legacy alias.
 *
 * Returns true when at least one field was set — the signal a caller uses to
 * decide between field-level errors and a form-level fallback message.
 */
export function mapServerErrors(
  problem: ServerErrorPayload | null | undefined,
  setError: SetFieldError,
): boolean {
  if (!problem) return false;
  const source = problem.errors ?? problem.validationErrors;
  if (!source || typeof source !== 'object') return false;
  let touched = false;
  for (const [field, messages] of Object.entries(source)) {
    if (!Array.isArray(messages) || messages.length === 0) continue;
    const firstMessage = messages[0];
    if (typeof firstMessage !== 'string') continue;
    setError(field, firstMessage);
    touched = true;
  }
  return touched;
}

// ─── Optimistic rollback ──────────────────────────────────────────────

export interface OptimisticMutationOpts<TData, TVars> {
  /**
   * Snapshot helper — called inside `onMutate` BEFORE the optimistic
   * update is applied. Returns a token consumed by `revert` on error.
   */
  snapshot: () => TData | undefined;
  /**
   * Apply the optimistic update locally. Called between `snapshot()` and
   * the network request.
   */
  applyOptimistic: (vars: TVars) => void;
  /**
   * Roll back to the snapshotted state if the mutation fails. Receives
   * the value returned by `snapshot()`.
   */
  revert: (previous: TData | undefined) => void;
}

export interface UseOptimisticRollbackHelpers<TData, TVars> {
  /** Run on mutation start; returns the snapshot for `onError` use. */
  onMutate: (vars: TVars) => TData | undefined;
  /** Run on mutation failure with the snapshot returned by `onMutate`. */
  onError: (err: unknown, vars: TVars, previous: TData | undefined) => void;
}

/**
 * The whole behaviour of `useOptimisticRollback`, as a pure factory.
 *
 * The hook is only this plus memoisation, which is what lets the test stub
 * share the shipped semantics verbatim instead of guessing at them.
 */
export function buildOptimisticRollback<TData, TVars>(
  opts: OptimisticMutationOpts<TData, TVars>,
): UseOptimisticRollbackHelpers<TData, TVars> {
  const { snapshot, applyOptimistic, revert } = opts;
  return {
    onMutate: (vars: TVars) => {
      const previous = snapshot();
      applyOptimistic(vars);
      return previous;
    },
    onError: (_err: unknown, _vars: TVars, previous: TData | undefined) => {
      revert(previous);
    },
  };
}
